/**
 * Single source of truth for every tunable used by the live Vibe Verification
 * camera flow (face-pose only — front / left / right). Do not hard-code any
 * of these values elsewhere — change them here only.
 *
 * Replaces the former shaka-gesture flow (shakaConfig.ts) entirely — see
 * VibeVerificationCameraModal.tsx / useVibeVerificationStateMachine.ts. No
 * hand gesture is required anywhere in this flow anymore.
 */

/** How often we ask the native module to analyze a fresh frame, in ms. Not per-frame. */
export const FRAME_SAMPLE_INTERVAL_MS = 400;

/**
 * Yaw (headEulerAngleY, ML Kit convention) magnitude, in degrees, below
 * which a face counts as "looking straight ahead" for the FRONT pose.
 * Generous enough that a genuinely front-facing user isn't rejected for
 * minor natural head tilt — this is a "roughly straight," not "exactly 0°,"
 * gate.
 */
export const FRONT_YAW_TOLERANCE_DEG = 15;

/**
 * Yaw magnitude required to count as a valid LEFT/RIGHT profile turn.
 * Reuses this app's one existing real-device-calibrated head-turn
 * precedent (the former liveness challenge's HEAD_TURN_ANGLE_THRESHOLD_DEG
 * was 18°) as the lower bound, and caps the upper bound well short of a
 * full 90° profile, where ML Kit's face detector reliability degrades
 * sharply (per Face Detection docs/behavior — a near-profile face loses
 * most of the landmarks the "FAST" performance-mode detector relies on).
 * 30–55° sits inside the range explicitly suggested for this flow: enough
 * to be an unambiguous, deliberate turn, not so much that detection itself
 * becomes unreliable.
 */
export const PROFILE_YAW_MIN_DEG = 30;
export const PROFILE_YAW_MAX_DEG = 55;

/**
 * Sign convention for THIS pipeline's captured frames (ML Kit
 * `Face.headEulerAngleY`, read via `InputImage.fromFilePath` on a
 * VisionCamera front-camera JPEG). The old liveness-challenge code's
 * "negative = user's left" assumption, previously copied here, was wrong
 * for this specific capture path and produced systematically flipped
 * LEFT/RIGHT labels on a real device (confirmed via the FRONT/LEFT/RIGHT
 * live test) — VisionCamera's front-camera photo output is NOT in the
 * same orientation/mirroring convention the old liveness code's image
 * source used. Empirically corrected: POSITIVE angle = the user's head is
 * physically turned to THEIR left; NEGATIVE = turned to their right.
 */
export const LEFT_YAW_SIGN = 1;
export const RIGHT_YAW_SIGN = -1;

/** Pitch/roll tolerance (headEulerAngleX / headEulerAngleZ), in degrees, for every pose. */
export const PITCH_TOLERANCE_DEG = 20;
export const ROLL_TOLERANCE_DEG = 20;

/**
 * Consecutive analysis ticks where the required pose was valid before
 * auto-capturing — prevents a single noisy/lucky frame from triggering a
 * capture. At FRAME_SAMPLE_INTERVAL_MS=400ms, 2 consecutive ticks ≈ 800ms
 * of continuous, genuine pose-holding — inside the "~500-800ms" window this
 * flow targets, without requiring the user to freeze for several seconds.
 * Any invalid tick resets the counter to 0.
 */
export const POSE_STABLE_TICKS_REQUIRED = 2;

/** Delay after a pose is confirmed stable, purely so the "✓ Captured" UI is readable before advancing. */
export const CAPTURE_CONFIRM_DELAY_MS = 500;

/** Hard cap on total time spent in the live camera view before offering the manual-review fallback. */
export const VERIFICATION_SESSION_TIMEOUT_MS = 60_000;
